import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, Zap, Eye, Bell, User } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUnseenAlertsCount } from "@/hooks/useUnseenAlertsCount";

const links = [
  { to: "/", icon: Home, label: "Decide" },
  { to: "/feed", icon: Zap, label: "Feed" },
  { to: "/watchlist", icon: Eye, label: "Watch" },
  { to: "/alerts", icon: Bell, label: "Alerts" },
  { to: "/profile", icon: User, label: "Profile" },
];

export const DECIDE_RESET_EVENT = "decyzon:reset-decide";

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const unseen = useUnseenAlertsCount();

  if (location.pathname === "/auth") return null;
  if (!user && ["/privacy", "/terms", "/refunds", "/contact", "/unsubscribe"].includes(location.pathname)) return null;

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-border/50 bg-background/95 backdrop-blur-md safe-area-bottom">
      <div className="flex items-stretch justify-around max-w-lg mx-auto h-[56px]">
        {links.map((link) => {
          const isActive = location.pathname === link.to;
          const showBadge = link.to === "/alerts" && unseen > 0;
          // "Decide" is a true session reset, not just a route nav. If already on /
          // we fire the reset event (Index listens). Otherwise we navigate with ?reset=1.
          const isDecide = link.to === "/";
          const handleClick = isDecide
            ? (e: React.MouseEvent) => {
                e.preventDefault();
                if (location.pathname === "/") {
                  window.dispatchEvent(new CustomEvent(DECIDE_RESET_EVENT));
                } else {
                  navigate("/?reset=1");
                }
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            : undefined;
          return (
            <NavLink
              key={link.to}
              to={link.to}
              onClick={handleClick}
              className={`flex flex-col items-center justify-center gap-0.5 min-w-[60px] min-h-[44px] relative transition-colors ${
                isActive ? "text-primary" : "text-muted-foreground/90 active:text-foreground"
              }`}
            >
              {isActive && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-[2px] rounded-full bg-primary" />}
              <div className="relative">
                <link.icon className="w-[18px] h-[18px]" strokeWidth={isActive ? 2.2 : 1.5} />
                {showBadge && (
                  <span className="absolute -top-1 -right-2 min-w-[14px] h-[14px] px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-body font-medium flex items-center justify-center">
                    {unseen > 9 ? "9+" : unseen}
                  </span>
                )}
              </div>
              <span className={`text-[10px] font-body ${isActive ? "font-medium" : ""}`}>{link.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
